import { type } from "@/api/arktype";

import { ITEM_STATES, PRONTUARIO_SECTIONS } from "@/api/medico/taxonomy";
import { pagination } from "@/lib/pagination";

const transcriptRef = type({ transcriptId: "string > 0" });
const jobRef = type({ id: "number.integer > 0" });

const transcriptClass = type.enumerated("first", "return", "dictation", "not_consultation");

const sectionKey = type.enumerated(...PRONTUARIO_SECTIONS);
const itemState = type.enumerated(...ITEM_STATES);

const sectionItem = type({
	key: "string > 0",
	value: "string > 0",
	state: itemState,
	"evidence?": "string > 0",
	"original_term?": "string > 0",
	"note?": "string > 0",
});

const MAX_PASTE_BYTES = 500 * 1024;

const paste = type({ title: "string.trim", text: "string.trim" }).narrow((input, ctx) => {
	if (input.title.length === 0) {
		return ctx.reject({ expected: "a non-empty title", actual: "blank" });
	}

	if (input.text.length === 0) {
		return ctx.reject({ expected: "a non-empty text", actual: "blank" });
	}

	if (new TextEncoder().encode(input.text).byteLength > MAX_PASTE_BYTES) {
		return ctx.reject({ expected: "text up to 500KB", actual: "larger" });
	}

	return true;
});

const byPerson = type({ patientId: "number.integer > 0" });

const link = type({
	transcriptId: "string > 0",
	"medPatientId?": "number.integer > 0",
	"patientId?": "number.integer > 0",
	class: transcriptClass,
	consentRecording: "boolean",
	"groupId?": "string > 0",
});

export const MedicoSchemas = {
	sync: type({ force: "boolean = false" }),
	transcripts: pagination,
	paste,
	link,
	ignore: transcriptRef,
	unignore: transcriptRef,
	unlink: transcriptRef,
	partialUnlink: transcriptRef,
	transcript: transcriptRef,
	relink: type({ medPatientId: "number.integer > 0", patientId: "number.integer > 0" }),
	byPerson,
	requeue: type({ medPatientId: "number.integer > 0", "groupId?": "string > 0" }),
	discardJob: jobRef,
	retryStage: type({ groupId: "string > 0" }),
	retryJob: jobRef,
	prontuario: type({ medPatientId: "number.integer > 0" }),
	editSection: type({
		medPatientId: "number.integer > 0",
		section: sectionKey,
		items: sectionItem.array(),
	}),
	validateSection: type({ medPatientId: "number.integer > 0", section: sectionKey }),
};

export type LinkInput = typeof link.infer;
export type PasteInput = typeof paste.infer;
export type ByPersonInput = typeof MedicoSchemas.byPerson.infer;
export type EditSectionInput = typeof MedicoSchemas.editSection.infer;
export type SectionItemInput = typeof sectionItem.infer;
